import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { createAdminProgram, updateAdminProgram } from '../../../shared/api/programs.services';
import { buildProgramId, buildProgramPagePath, slugify } from '../../../shared/lib/slugify';
import toast from '../../../shared/lib/toast';

const emptyText = () => ({ ua: '', en: '' });

const validationSchema = Yup.object({
  title: Yup.object({
    ua: Yup.string().required('Вкажіть назву програми'),
  }),
  slug: Yup.string()
    .required('Вкажіть адресу сторінки')
    .matches(/^[a-z0-9-]+$/, 'Адреса може містити лише латинські літери, цифри й дефіс'),
});

const toText = (value) => {
  if (typeof value === 'string') return { ua: value, en: value };
  if (value && typeof value === 'object') {
    return { ua: value.ua || '', en: value.en || '' };
  }
  return emptyText();
};

const toTextList = (list) => (Array.isArray(list) ? list.map(toText) : []);

const hasText = (value) => Boolean(value?.ua?.trim() || value?.en?.trim());

const cleanTextList = (list) => (list || []).filter(hasText);

// Блок, у которого не заполнено ничего, сохраняем как отсутствующий —
// тогда сайт подставит свой типовой текст, а не покажет пустоту.
const dropIfEmpty = (section, isEmpty) => (isEmpty ? undefined : section);

const buildInitialValues = (program) => {
  const page = program?.page || {};
  const hero = page.hero || {};
  const overview = page.overview || {};
  const methodology = page.methodology || {};
  const outcomes = page.outcomes || {};
  const highlight = page.highlightSection || {};
  const finalCta = page.finalCta || {};

  return {
    // Картка програми на головній
    slug: program?.slug || '',
    ageLabel: toText(program?.ageLabel),
    title: toText(program?.title),
    summary: toText(program?.summary),
    ctaLabel: toText(program?.ctaLabel),
    coverImageUrl: program?.coverImageUrl || '',
    sortOrder: typeof program?.sortOrder === 'number' ? program.sortOrder : 0,
    isFeatured: Boolean(program?.isFeatured),
    isActive: program?.isActive !== false,

    // Сторінка програми
    hero: {
      eyebrow: toText(hero.eyebrow),
      title: toText(hero.title),
      highlightedWord: toText(hero.highlightedWord),
      description: toText(hero.description),
      primaryCta: {
        label: toText(hero.primaryCta?.label),
        url: hero.primaryCta?.url || '',
      },
    },
    overview: {
      title: toText(overview.title),
      paragraphs: toTextList(overview.paragraphs),
      facts: (Array.isArray(overview.facts) ? overview.facts : []).map((fact) => ({
        label: toText(fact?.label),
        value: toText(fact?.value),
      })),
    },
    methodology: {
      title: toText(methodology.title),
      description: toText(methodology.description),
      items: (Array.isArray(methodology.items) ? methodology.items : []).map((item, index) => ({
        step: toText(item?.step || String(index + 1).padStart(2, '0')),
        title: toText(item?.title),
        text: toText(item?.text || item?.description),
      })),
    },
    outcomes: {
      title: toText(outcomes.title),
      description: toText(outcomes.description),
      items: (Array.isArray(outcomes.items) ? outcomes.items : []).map((item, index) => ({
        step: toText(item?.step || String(index + 1).padStart(2, '0')),
        label: toText(item?.label),
        title: toText(item?.title),
        subtitle: toText(item?.subtitle),
        isHighlighted: Boolean(item?.isHighlighted),
      })),
    },
    highlightSection: {
      title: toText(highlight.title),
      description: toText(highlight.description),
      audienceTitle: toText(highlight.audienceTitle),
      audienceItems: toTextList(highlight.audienceItems),
      reasonsTitle: toText(highlight.reasonsTitle),
      reasonsItems: toTextList(highlight.reasonsItems),
    },
    finalCta: {
      title: toText(finalCta.title),
      description: toText(finalCta.description),
      primaryCta: {
        label: toText(finalCta.primaryCta?.label),
        url: finalCta.primaryCta?.url || '',
      },
      secondaryCta: {
        label: toText(finalCta.secondaryCta?.label),
        url: finalCta.secondaryCta?.url || '',
      },
    },
  };
};

const buildPagePayload = (values, existingPage = {}) => {
  const highlight = values.highlightSection;
  const highlightIsEmpty =
    !hasText(highlight.title) &&
    !hasText(highlight.description) &&
    cleanTextList(highlight.audienceItems).length === 0 &&
    cleanTextList(highlight.reasonsItems).length === 0;

  const finalCta = values.finalCta;
  const finalCtaIsEmpty =
    !hasText(finalCta.title) &&
    !hasText(finalCta.description) &&
    !hasText(finalCta.primaryCta.label) &&
    !hasText(finalCta.secondaryCta.label);

  return {
    // Все, що форма не редагує (breadcrumbs і будь-що інше), лишається на
    // місці: раніше збереження контенту стирало highlightSection цілком.
    ...existingPage,
    hero: values.hero,
    overview: {
      title: values.overview.title,
      paragraphs: cleanTextList(values.overview.paragraphs),
      facts: (values.overview.facts || []).filter(
        (fact) => hasText(fact.label) || hasText(fact.value)
      ),
    },
    methodology: {
      title: values.methodology.title,
      description: values.methodology.description,
      items: (values.methodology.items || []).filter(
        (item) => hasText(item.title) || hasText(item.text)
      ),
    },
    outcomes: {
      title: values.outcomes.title,
      description: values.outcomes.description,
      items: (values.outcomes.items || []).filter(
        (item) => hasText(item.title) || hasText(item.label) || hasText(item.subtitle)
      ),
    },
    highlightSection: dropIfEmpty(
      {
        ...highlight,
        audienceItems: cleanTextList(highlight.audienceItems),
        reasonsItems: cleanTextList(highlight.reasonsItems),
      },
      highlightIsEmpty
    ),
    finalCta: dropIfEmpty(finalCta, finalCtaIsEmpty),
  };
};

export const useProgramsContentForm = (initialData, { mode = 'edit' } = {}) => {
  const navigate = useNavigate();
  const isCreate = mode === 'create';

  return useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(initialData),
    validationSchema,
    onSubmit: async (values) => {
      try {
        const slug = slugify(values.slug);

        if (!slug) {
          toast.error('Не вдалося скласти адресу сторінки — заповніть поле «Адреса сторінки»');
          return;
        }

        const payload = {
          slug,
          ageLabel: values.ageLabel,
          title: values.title,
          summary: values.summary,
          ctaLabel: values.ctaLabel,
          coverImageUrl: values.coverImageUrl,
          sortOrder: Number(values.sortOrder) || 0,
          isFeatured: values.isFeatured,
          isActive: values.isActive,
          page: buildPagePayload(values, initialData?.page || {}),
        };

        if (isCreate) {
          // Сервер вимагає id, pagePath і page — без них створення падало
          // на валідації моделі, і жодна нова програма не зберігалась.
          const response = await createAdminProgram({
            ...payload,
            id: buildProgramId(slug),
            pagePath: buildProgramPagePath(slug),
          });

          if (response?.error) throw new Error(response.message || 'Помилка при створенні');

          toast.success('Програму створено — тепер заповніть решту розділів');
          navigate(`/programs-content-edit/${response?.id || buildProgramId(slug)}`);
          return;
        }

        const programId = initialData?._id || initialData?.id;
        if (!programId) throw new Error('ID програми не знайдено');

        const response = await updateAdminProgram(programId, {
          ...payload,
          pagePath: initialData?.pagePath || buildProgramPagePath(slug),
        });
        if (response?.error) throw new Error(response.message || 'Помилка при оновленні');

        toast.success('Програму збережено!');
        navigate('/programs');
      } catch (error) {
        console.error('Помилка при збереженні:', error);
        toast.error(error.message || 'Сталася помилка. Спробуйте ще раз.');
      }
    },
  });
};

export { buildInitialValues, buildPagePayload };
